import { DEFAULT_MEMBERS_SORT_BY, DEFAULT_TEAM_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { isMembersSortField } from "@custom-types/type-guards";
import { isProjectMemberBase } from "@custom-types/member-type-guards";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { getMembersSortBy, getTeamMembersSortBy } from "@utils/member-utils";
import { convertGridSortModelToSortBy } from "@utils/sort-utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { getHeldSeats, getSeatsRank } from "@utils/member-seats-utils";

/** All the possible headers for members table */
export let MemberHeaders = /*#__PURE__*/function (MemberHeaders) {
  /** User avatar */
  MemberHeaders["avatar"] = "Avatar";
  /** User full name */
  MemberHeaders["user"] = "User";
  /** User full name or team name */
  MemberHeaders["name"] = "Name";
  /** User email */
  MemberHeaders["email"] = "Email";
  /** User status */
  MemberHeaders["status"] = "Status";
  /** User role */
  MemberHeaders["role"] = "Role";
  /** Whether the entry is a member or a team */
  MemberHeaders["type"] = "Type";
  /** Date in which a member was added to a group */
  MemberHeaders["joinedDate"] = "Added date";
  /** The seats of the workspace subscription that the member holds */
  MemberHeaders["seats"] = "Seats";
  /** Any other possible options */
  MemberHeaders["options"] = "Options";
  return MemberHeaders;
}({});

/** Returns the specific role column header according to the table context type */
export function getRoleHeader(tableSubject) {
  switch (tableSubject) {
    case "workspace":
    case "activeMembers":
      return "Workspace Role";
    case "project":
      return "Project Role";
    default:
      return "Role";
  }
}

/** The display name to show representing memberTableSubject */
export function getMemberTableSubjectDisplayName(memberTableSubject) {
  switch (memberTableSubject) {
    case "project":
    case "group":
    case "workspace":
    case "snapshot":
      return memberTableSubject;
    case "team":
      return TEAM_DISPLAY_NAME;
    case "workspaceTeam":
      return "workspace";
    case "activeMembers":
      return "active members";
  }
}

/** Returns the subject name for Amplitude tracking based on table context */
export function getMemberTableTrackingSubject(tableSubject) {
  switch (tableSubject) {
    case "workspace":
      return "Workspace Member";
    case "project":
      return "Project Member";
    case "group":
      return "Group Member";
    case "team":
      return "Team Member";
    case "activeMembers":
      return "Active Member";
    default:
      return "Member";
  }
}

/** Normalizes a member name for sorting, returning null when empty. */
function normalizeMemberName(name) {
  const trimmedName = name?.trim();
  return trimmedName && trimmedName.length > 0 ? trimmedName : null;
}

/**
 * Compares two members by their normalized name.
 * Empty names are placed last, non-empty names use numeric locale comparison.
 *
 * @param memberA - First member to compare
 * @param memberB - Second member to compare
 * @param order - Sort order: "ascending" (default) or "descending"
 * @returns Negative if memberA < memberB, positive if memberA > memberB, 0 if equal
 */
export function compareMembersByDefaultName(memberA, memberB, order = "ascending") {
  const nameA = normalizeMemberName(memberA.name);
  const nameB = normalizeMemberName(memberB.name);
  if (!nameA || !nameB) {
    if (!nameA && !nameB) {
      return 0;
    }
    return nameA ? -1 : 1;
  }
  const comparison = nameA.localeCompare(nameB, undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    numeric: true,
    sensitivity: "base"
  });
  return order === "ascending" ? comparison : -comparison;
}

/**
 * Compares two members by their status for sorting.
 * user-group members (teams) always go to the end.
 * @param memberA - First member to compare
 * @param memberB - Second member to compare
 * @param order - Sort order: "ascending" (active first, then pending) or "descending" (pending first, then active)
 */
export function compareMembersByStatus(memberA, memberB, order) {
  const isATeam = FaroCoreApiUtils.Member.isTeam(memberA);
  const isBTeam = FaroCoreApiUtils.Member.isTeam(memberB);

  // Teams always go to the end
  if (isATeam && !isBTeam) {
    return order === "ascending" ? 1 : -1;
  }
  if (!isATeam && isBTeam) {
    return order === "ascending" ? -1 : 1;
  }
  if (isATeam && isBTeam) {
    return 0;
  }

  // Both have status: sort by active/pending
  const isAActive = FaroCoreApiUtils.Member.isMemberActive(memberA);
  const isBActive = FaroCoreApiUtils.Member.isMemberActive(memberB);
  const comparison = Number(isBActive) - Number(isAActive);
  return order === "ascending" ? comparison : -comparison;
}

/**
 * Compares two members by the seats they hold, so that the seat holders are grouped together
 * instead of being scattered across the table. See getSeatsRank for how the seats are ranked.
 *
 * Teams need no case of their own: they can never hold a seat and therefore sort with the members
 * that hold none.
 *
 * @param memberA First member to compare
 * @param memberB Second member to compare
 * @param seatsLookup The seats of the workspace
 * @param order Sort order: "ascending" (most seats first) or "descending" (most seats last)
 */
export function compareMembersBySeats(memberA, memberB, seatsLookup, order) {
  const comparison = getSeatsRank(getHeldSeats(memberB, seatsLookup)) - getSeatsRank(getHeldSeats(memberA, seatsLookup));
  return order === "ascending" ? comparison : -comparison;
}

/**
 * @returns a valid members `sortBy` option from the passed GridSortModel
 * @param gridSortModel Members table sort model
 */
export function getMembersSortByFromGridSortModel(gridSortModel) {
  return convertGridSortModelToSortBy(gridSortModel, {
    defaultSortBy: DEFAULT_MEMBERS_SORT_BY,
    isValidSortField: isMembersSortField,
    getSortBy: getMembersSortBy
  });
}

/**
 * @returns a valid members `sortBy` option from the passed GridSortModel
 * @param gridSortModel Members table sort model
 */
export function getTeamMembersSortByFromGridSortModel(gridSortModel) {
  return convertGridSortModelToSortBy(gridSortModel, {
    defaultSortBy: DEFAULT_TEAM_MEMBERS_SORT_BY,
    isValidSortField: isMembersSortField,
    getSortBy: getTeamMembersSortBy
  });
}

/**
 * Checks if a specific field is visible for a given table subject.
 * @param field The field to check visibility for.
 * @param tableSubject The table subject context.
 * @returns True if the field is visible, false otherwise.
 */
export function isFieldVisibleForTableSubject(field, tableSubject) {
  const tablesWithUserColumn = new Set(["workspace", "activeMembers", "team"]);

  /* eslint-disable @typescript-eslint/naming-convention */
  const fieldVisibility = {
    user: tablesWithUserColumn.has(tableSubject),
    name: !tablesWithUserColumn.has(tableSubject),
    joinedDate: tableSubject === "group",
    // Only the workspace members table has the seats column
    seats: tableSubject === "workspace"
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  return fieldVisibility[field] ?? true;
}
/** Whether the given member can be removed from the project */
export function canRemoveMember({
  member,
  hasRequiredIds,
  canRemoveFromProject,
  canDeleteFromCompany
}) {
  if (!hasRequiredIds) {
    return false;
  }
  const isResourceOwner = isProjectMemberBase(member) && member.role === "RESOURCE_OWNER";
  return !isResourceOwner && (canRemoveFromProject || canDeleteFromCompany);
}